import { Play, Pause, RotateCcw, XCircle, Trash2, Loader2 } from "lucide-react";
import { WebScrapeJob, WebScraperJobStatus } from "../../../services/web-scraper.service";

interface JobActionButtonsProps {
  job: WebScrapeJob;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isLoading: boolean;
  loadingAction?: string;
}

export default function JobActionButtons({
  job,
  onStart,
  onPause,
  onResume,
  onCancel,
  onDelete,
  isLoading,
  loadingAction,
}: JobActionButtonsProps) {
  const spinner = <Loader2 className="w-4 h-4 animate-spin" />;

  const btn = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    className: string,
    action: string,
    title: string,
  ) => (
    <button
      key={action}
      onClick={onClick}
      disabled={isLoading}
      title={title}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      type="button"
    >
      {isLoading && loadingAction === action ? spinner : icon}
      {label}
    </button>
  );

  const { status } = job;

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {status === WebScraperJobStatus.NOT_STARTED &&
        btn("Start", <Play className="w-4 h-4" />, onStart, "bg-green-600 text-white hover:bg-green-700", "start",
          "Begin the crawl. The job will fetch the seed URL and follow links up to your max-depth setting.")}

      {status === WebScraperJobStatus.RUNNING && (
        <>
          {btn("Pause", <Pause className="w-4 h-4" />, onPause, "bg-yellow-500 text-white hover:bg-yellow-600", "pause",
            "Stop the crawl at the next safe checkpoint. You can resume later from where it stopped.")}
          {btn("Cancel", <XCircle className="w-4 h-4" />, onCancel, "bg-red-600 text-white hover:bg-red-700", "cancel",
            "Permanently stop this crawl. Marks it Failed — cannot be resumed.")}
        </>
      )}

      {status === WebScraperJobStatus.PAUSED && (
        <>
          {btn("Resume", <RotateCcw className="w-4 h-4" />, onResume, "bg-green-600 text-white hover:bg-green-700", "resume",
            "Continue the crawl from where it was paused. Already-visited URLs are skipped automatically.")}
          {btn("Cancel", <XCircle className="w-4 h-4" />, onCancel, "bg-red-600 text-white hover:bg-red-700", "cancel",
            "Permanently stop this crawl. Marks it Failed — cannot be resumed.")}
        </>
      )}

      {status === WebScraperJobStatus.DONE &&
        btn("Delete", <Trash2 className="w-4 h-4" />, onDelete, "border border-red-300 text-red-600 hover:bg-red-50", "delete",
          "Remove this job and all its discovered items. Imported documents stay in your library.")}

      {status === WebScraperJobStatus.FAILED &&
        btn("Delete", <Trash2 className="w-4 h-4" />, onDelete, "border border-red-300 text-red-600 hover:bg-red-50", "delete",
          "Remove this failed job. Already-imported documents stay in your library.")}
    </div>
  );
}
